// "My open items": every concierge to-do due today or within 7 days across
// the athletes you manage, soonest first, grouped by athlete (a task waiting
// on someone still needs a due date, and it shows the same ⏳ badge as the
// athlete panel — it just doesn't extend the window). Mine = athletes whose
// concierge owner is you. Staff can switch to All.
// Same one-axis-is-time bucketing as the athlete panel's TodoPanel — see
// docs/superpowers/specs/2026-09-19-concierge-todo-task-model-design.md §7.
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';
import {
  bucketTasks, describeTask, groupByAthlete, isOverdue, todoErrorText, utcTodayIso,
} from '../lib/conciergeTodoModel';
import '../components/Layout.css';
import '../components/athleteEditor/AthleteEditor.css';
import './ConciergePage.css';

const BUCKETS = [
  ['overdue', 'Overdue'],
  ['upcoming', 'Upcoming'],
  ['later', 'Later'],
];

export default function ConciergeTodoQueuePage() {
  const navigate = useNavigate();
  const { API_URL, user: me } = useAuth();
  const isStaff = ['admin', 'internal_staff'].includes(me?.role);
  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const [scope, setScope] = useState('mine');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Guards against a slower, now-superseded request landing after a newer
  // one — e.g. toggling Mine -> All fires a second request while the first
  // is still in flight; only the response matching the latest reqId applies.
  const reqId = useRef(0);
  // One check-off at a time: the ref stops a same-tick second click, the
  // state disables the buttons until the write and its reload land.
  const saving = useRef(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const myId = ++reqId.current;
    setLoading(true);
    return axios.get(`${API_URL}/concierge/todos/queue`, { ...cfg(), params: { scope } })
      .then(res => { if (myId === reqId.current) setItems(res.data?.items || []); })
      .catch(err => { if (myId === reqId.current) setError(todoErrorText(err, 'Failed to load open items.')); })
      .finally(() => { if (myId === reqId.current) setLoading(false); });
  };

  useEffect(() => {
    // Drop the previous scope's items immediately so the loading state shows
    // instead of a stale list while the new scope's request is in flight.
    setItems([]);
    setError('');
    load();
    /* eslint-disable-next-line */
  }, [API_URL, scope]);

  const athletePage = (id) => `/athletes/${id}/concierge#todos`;

  // The queue page has no Done dialog to ask a recurring task's next due
  // date, so closing here always ends the repeat chain (no next_due_at) —
  // a judgement call, not a limitation to route around. Anything richer
  // (the note-taking done dialog, a chosen next date) belongs on the
  // athlete panel's TodoPanel, which is why the control reads "Close" and
  // never implies a repeat continues.
  const close = (athleteUserId, t) => {
    // The monthly summary cannot be closed from here: the server requires a
    // body (concierge_todos.py:172-175) and the body editor lives only in
    // the athlete panel's DoneDialog. Send them there instead of firing a
    // PATCH that always comes back 400.
    if (t.template_key === 'monthly_summary') { navigate(athletePage(athleteUserId)); return; }
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError('');
    axios.patch(`${API_URL}/concierge/${athleteUserId}/todos/${t.id}`, { action: 'done' }, cfg())
      .then(load)
      .catch(err => setError(todoErrorText(err, 'Save failed.')))
      .finally(() => { saving.current = false; setBusy(false); });
  };

  const today = utcTodayIso();
  const g = bucketTasks(items, today);
  const empty = g.overdue.length + g.upcoming.length + g.later.length === 0;

  return (
    <AdminPage active="concierge" title="My open items"
               subtitle="Overdue and due within 7 days — across your concierge athletes">
      <div className="cb-wrap">
        <div className="cb-controls">
          <button type="button" className="cb-controls__clear" onClick={() => navigate('/concierge')}>← Board</button>
          {isStaff && (
            <>
              <button type="button" className="cb-chip" aria-pressed={scope === 'mine'} onClick={() => setScope('mine')}>Mine</button>
              <button type="button" className="cb-chip" aria-pressed={scope === 'all'} onClick={() => setScope('all')}>All athletes</button>
            </>
          )}
        </div>
        {error && <div className="cn-inline-msg cn-inline-msg--err">{error}</div>}
        {loading && items.length === 0 ? (
          <div className="cn-loading">Loading…</div>
        ) : empty ? (
          <div className="cn-empty">Nothing overdue or due soon.</div>
        ) : BUCKETS.map(([k, label]) => (g[k].length > 0 && (
          <div key={k} className="cn-todo-group" data-testid={`queue-bucket-${k}`}>
            <h4 className={`cn-todo-group__title cn-todo-group__title--${k}`}>{label}</h4>
            {groupByAthlete(g[k]).map(grp => (
              <div key={grp.athlete_user_id} className="cq-athlete">
                <Link className="cq-athlete__name" to={athletePage(grp.athlete_user_id)}>
                  {grp.athlete_name}
                </Link>
                <ul className="cn-todo-list">
                  {grp.items.map(t => (
                    <li key={t.id}
                        className={`cn-todo cn-todo--${t.status}${isOverdue(t, today) ? ' cn-todo--overdue' : ''}`}>
                      <div className="cn-todo__main">
                        <button type="button" className="cn-todo__check"
                                aria-label={t.template_key === 'monthly_summary'
                                  ? `Write summary for ${t.title}` : `Close ${t.title}`}
                                disabled={busy}
                                onClick={() => close(grp.athlete_user_id, t)} />
                        <div className="cn-todo__text">
                          <div className="cn-todo__title">{t.title}</div>
                          <div className="cn-todo__sub">
                            {describeTask(t)}
                            {t.waiting_on && <span className="cn-todo__badge">⏳ {t.waiting_on}</span>}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )))}
      </div>
    </AdminPage>
  );
}
